import React from 'react';
import PropTypes from 'prop-types';
import SingleLine from './SingleLine';
import TextBox from './TextBox';
import MultiLineInput from './MultiLineInput';
import CheckboxQuestion from './CheckboxQuestion';
import AdditionalNotes from './AdditionalNotes';
import MultipleChoice from './MultipleChoice';
import SingleChoice from './SingleChoice';
import Attachment from './Attachment';
import Signature from './Signature';
import BoxGrid from './BoxGrid';
import Dropdown from './Dropdown';
import LinearScale from './LinearScale';
import Image from './Image';
import {FormBuilderComponentTypes} from '../ComponentList';
import OfferStatusChoice from './OfferStatusChoice';
import FileUploader from './FileUploader';
import DateTime from './DateTime';
import Date from './Date';
import AppointmentBooking from './AppointmentBooking';
import Payment from './Payment';
import StripePayment from './StripePayment';
import LookupDropdown from './LookupDropdown';
import Address from './Address';
import IntakeChoice from './IntakeChoice';
import BoarderStatusChoice from './BoarderStatusChoice';
import NumericInputBox from './NumericInputBox';
import QuestionGrid from './QuestionGrid';
import Separator from './Separator';
import {FORM_ACCESS_LEVELS} from '../../../../../constants/formAccessLevels';
import UPN from './UPN';
import PreviousSchoolName from './PreviousSchoolName';
import PreviousSchoolEmail from './PreviousSchoolEmail';
import EmployeeSelect from './EmployeeSelect';
import StudentPhoto from './StudentPhoto';
import Calculation from './Calculation';
import MedicineDispense from './MedicineDispense';
import MedicineStock from './MedicineStock';
import PrefillFromSettings, {PREFILLABLE_COMPONENT_TYPES} from './Shared/PrefillFromSettings';
import {Box} from '@mui/material';

const ComponentBuilder = ({
                              index,
                              readOnly,
                              previewMode,
                              formPreviewMode,
                              handleChangeEdit,
                              handleChangeSubmit,
                              handleDownloadAttachment,
                              submitData,
                              data,
                              submitMode,
                              field,
                              isFormArchived,
                              placeholderValues,
                              accessLevel,
                              relatedComponent,
                              componentToEditIndex,
                              error,
                              audience,
                              parentAudienceType,
                              recipientType,
                              linkedToStudents,
                              linkedToEmployees,
                              formRequestId,
                              formId,
                              schema,
                              onEmailCheck,
                              setWarningFor,
                              warnings,
                              getDraftDescriptionHtml,
                              setDraftDescriptionHtml,
                              commitDraftDescriptionToSchema,
                          }) => {

    const renderComponent = () => {
    switch (field.type) {
        case 'singleText': {
            const fieldId = field.id;
            const isEmailField =
                field.inputType === 'email' ||
                field.key === 'contactEmail' ||
                field.enableEmailCheck === true;
            const fallbackDescriptionHtml = field.formattedDescription || field.description || '';
            const descriptionHtml = getDraftDescriptionHtml(fieldId, fallbackDescriptionHtml);
            return (
                <SingleLine
                    onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                    index={index}
                    componentId={fieldId}
                    fieldKey={field.key}
                    value={readOnly ? data[fieldId] : submitData[fieldId]}
                    descriptionHtml={descriptionHtml}
                    onDescriptionHtmlChange={(nextHtml) => setDraftDescriptionHtml(fieldId, nextHtml)}
                    commitDescriptionHtml={() => commitDraftDescriptionToSchema(fieldId)}
                    enableValidation={field.enableValidation}
                    accessLevel={accessLevel}
                    isEditMode={!submitMode && !previewMode}
                    isFormArchived={isFormArchived}
                    readonly={readOnly}
                    placeholderValues={placeholderValues}
                    submitMode={submitMode}
                    type={field.inputType}
                    formRequestId={formRequestId}
                    onEmailCheck={isEmailField ? onEmailCheck : undefined}
                    warning={warnings?.[fieldId] ?? ''}
                    setWarning={(msg) => setWarningFor(fieldId, msg)}
                    error={error}
                    audience={audience}
                    recipientType={recipientType}
                    linkedToStudents={linkedToStudents}
                    linkedToEmployees={linkedToEmployees}
                />
            );
        }
        case 'textBox': {
            const fallbackDescriptionHtml = field.formattedDescription || field.description || '';
            const descriptionHtml = getDraftDescriptionHtml(field.id, fallbackDescriptionHtml);
            return (
                <TextBox
                    onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                    index={index}
                    componentId={field.id}
                    value={readOnly ? data[field.id] : submitData[field.id]}
                    descriptionHtml={descriptionHtml}
                    onDescriptionHtmlChange={(nextHtml) => setDraftDescriptionHtml(field.id, nextHtml)}
                    commitDescriptionHtml={() => commitDraftDescriptionToSchema(field.id)}
                    enableValidation={field.enableValidation}
                    accessLevel={accessLevel}
                    isEditMode={!submitMode && !previewMode}
                    isFormArchived={isFormArchived}
                    readonly={readOnly}
                    placeholderValues={placeholderValues}
                    submitMode={submitMode}
                    error={error}
                    audience={audience}
                    recipientType={recipientType}
                    linkedToStudents={linkedToStudents}
                    linkedToEmployees={linkedToEmployees}
                />
            );
        }
        case 'numericInputBox': {
            const fallbackDescriptionHtml = field.formattedDescription || field.description || '';
            const descriptionHtml = getDraftDescriptionHtml(field.id, fallbackDescriptionHtml);
            return (
                <NumericInputBox
                    onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                    index={index}
                    componentId={field.id}
                    value={readOnly ? data[field.id] : submitData[field.id]}
                    priority={field.priority}
                    descriptionHtml={descriptionHtml}
                    onDescriptionHtmlChange={(nextHtml) => setDraftDescriptionHtml(field.id, nextHtml)}
                    commitDescriptionHtml={() => commitDraftDescriptionToSchema(field.id)}
                    enableValidation={field.enableValidation}
                    accessLevel={accessLevel}
                    isEditMode={!submitMode && !previewMode}
                    isFormArchived={isFormArchived}
                    readonly={readOnly}
                    placeholderValues={placeholderValues}
                    submitMode={submitMode}
                    error={error}
                    showEditModeOnly={field.showEditModeOnly}
                    fieldKey={field.fieldKey ?? field.key}
                    audience={audience}
                    recipientType={recipientType}
                    linkedToStudents={linkedToStudents}
                    linkedToEmployees={linkedToEmployees}
                />
            );
        }
        case FormBuilderComponentTypes.MedicineDispense: {
            return (
                <MedicineDispense
                    onChange={!submitMode && !previewMode
                        ? (change) => handleChangeEdit({index, ...change})
                        : handleChangeSubmit}
                    componentId={field.id}
                    value={readOnly ? data[field.id] : submitData[field.id]}
                    label={field.label}
                    medicineLookupId={field.medicineLookupId}
                    isEditMode={!submitMode && !previewMode}
                    readonly={readOnly}
                    submitMode={submitMode}
                    previewMode={previewMode}
                    placeholderValues={placeholderValues}
                />
            );
        }
        case FormBuilderComponentTypes.MedicineStock: {
            return (
                <MedicineStock
                    onChange={!submitMode && !previewMode
                        ? (change) => handleChangeEdit({index, ...change})
                        : handleChangeSubmit}
                    componentId={field.id}
                    value={readOnly ? data[field.id] : submitData[field.id]}
                    label={field.label}
                    isEditMode={!submitMode && !previewMode}
                    readonly={readOnly}
                    submitMode={submitMode}
                    previewMode={previewMode}
                    placeholderValues={placeholderValues}
                    error={error}
                />
            );
        }
        case FormBuilderComponentTypes.Calculation: {
            const fallbackDescriptionHtml = field.formattedDescription || field.description || '';
            const descriptionHtml = getDraftDescriptionHtml(field.id, fallbackDescriptionHtml);
            return (
                <Calculation
                    onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                    index={index}
                    componentId={field.id}
                    value={readOnly ? data[field.id] : submitData[field.id]}
                    operands={field.operands}
                    schema={schema}
                    answers={readOnly ? data : submitData}
                    descriptionHtml={descriptionHtml}
                    onDescriptionHtmlChange={(nextHtml) => setDraftDescriptionHtml(field.id, nextHtml)}
                    commitDescriptionHtml={() => commitDraftDescriptionToSchema(field.id)}
                    accessLevel={accessLevel}
                    isEditMode={!submitMode && !previewMode}
                    isFormArchived={isFormArchived}
                    readonly={readOnly}
                    placeholderValues={placeholderValues}
                    submitMode={submitMode}
                    audience={audience}
                    recipientType={recipientType}
                    linkedToStudents={linkedToStudents}
                    linkedToEmployees={linkedToEmployees}
                />
            );
        }
        case 'multiLineInput': {
            const fallbackDescriptionHtml = field.formattedDescription || field.description || '';
            const descriptionHtml = getDraftDescriptionHtml(field.id, fallbackDescriptionHtml);
            return (
                <MultiLineInput
                    index={index}
                    onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                    componentId={field.id}
                    fieldKey={field.key}
                    descriptionHtml={descriptionHtml}
                    onDescriptionHtmlChange={(nextHtml) => setDraftDescriptionHtml(field.id, nextHtml)}
                    commitDescriptionHtml={() => commitDraftDescriptionToSchema(field.id)}
                    isEditMode={!submitMode && !previewMode}
                    isFormArchived={isFormArchived}
                    accessLevel={accessLevel}
                    audience={audience}
                    recipientType={recipientType}
                    linkedToStudents={linkedToStudents}
                    linkedToEmployees={linkedToEmployees}
                    value={readOnly ? data[field.id] : submitData[field.id]}
                    onValueChange={(nextValue) => handleChangeSubmit({
                        id: field.id,
                        value: nextValue
                    })}
                    enableValidation={field.enableValidation}
                    onEnableValidationChange={(next) => handleChangeEdit({
                        index,
                        enableValidation: next
                    })}
                    inputs={field.inputs}
                    onInputsChange={(patch) => handleChangeEdit({
                        index,
                        listKey: 'inputs',
                        ...patch
                    })}
                    readonly={readOnly}
                    placeholderValues={placeholderValues}
                    submitMode={submitMode}
                    error={error}
                />
            );
        }
        case 'appointmentBooking': {
            const fallbackDescriptionHtml = field.formattedDescription || field.description || '';
            const descriptionHtml = getDraftDescriptionHtml(field.id, fallbackDescriptionHtml);

            return (
                <AppointmentBooking
                    onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                    componentId={field.id}
                    index={index}
                    descriptionHtml={descriptionHtml}
                    onDescriptionHtmlChange={(nextHtml) => setDraftDescriptionHtml(field.id, nextHtml)}
                    commitDescriptionHtml={() => commitDraftDescriptionToSchema(field.id)}
                    bookingLink={field.bookingLink}
                    isEditMode={!submitMode && !previewMode}
                    isFormArchived={isFormArchived}
                    placeholderValues={placeholderValues}
                    readOnly={readOnly}
                    accessLevel={accessLevel}
                    audience={audience}
                    recipientType={recipientType}
                    linkedToStudents={linkedToStudents}
                    linkedToEmployees={linkedToEmployees}
                    submitMode={submitMode}
                />
            );
        }

        case 'payment': {
            const fallbackDescriptionHtml = field.formattedDescription || field.description || '';
            const descriptionHtml = getDraftDescriptionHtml(field.id, fallbackDescriptionHtml);

            return (
                <Payment
                    index={index}
                    onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                    componentId={field.id}
                    descriptionHtml={descriptionHtml}
                    onDescriptionHtmlChange={(nextHtml) => setDraftDescriptionHtml(field.id, nextHtml)}
                    commitDescriptionHtml={() => commitDraftDescriptionToSchema(field.id)}
                    paymentLink={field.paymentLink}
                    isEditMode={!submitMode && !previewMode}
                    isFormArchived={isFormArchived}
                    placeholderValues={placeholderValues}
                    readOnly={readOnly}
                    accessLevel={accessLevel}
                    audience={audience}
                    recipientType={recipientType}
                    linkedToStudents={linkedToStudents}
                    linkedToEmployees={linkedToEmployees}
                    submitMode={submitMode}
                />
            );
        }

        case FormBuilderComponentTypes.StripePayment: {
            const fallbackDescriptionHtml = field.formattedDescription || field.description || '';
            const descriptionHtml = getDraftDescriptionHtml(field.id, fallbackDescriptionHtml);

            return (
                <StripePayment
                    index={index}
                    onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                    componentId={field.id}
                    descriptionHtml={descriptionHtml}
                    onDescriptionHtmlChange={(nextHtml) => setDraftDescriptionHtml(field.id, nextHtml)}
                    commitDescriptionHtml={() => commitDraftDescriptionToSchema(field.id)}
                    amountPence={field.amountPence}
                    value={readOnly ? data[field.id] : submitData[field.id]}
                    formRequestId={formRequestId}
                    enableValidation={field.enableValidation}
                    error={error}
                    isEditMode={!submitMode && !previewMode}
                    isFormArchived={isFormArchived}
                    placeholderValues={placeholderValues}
                    readOnly={readOnly}
                    accessLevel={accessLevel}
                    audience={audience}
                    recipientType={recipientType}
                    linkedToStudents={linkedToStudents}
                    linkedToEmployees={linkedToEmployees}
                    submitMode={submitMode}
                />
            );
        }

        case 'tickBoxGrid':
        case 'multipleChoiceGrid': {
            const fallbackDescriptionHtml = field.formattedDescription || field.description || '';
            const descriptionHtml = getDraftDescriptionHtml(field.id, fallbackDescriptionHtml);

            return (
                <BoxGrid
                    onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                    componentToEditIndex={componentToEditIndex}
                    index={index}
                    componentId={field.id}
                    value={readOnly ? data[field.id] : submitData[field.id]}
                    descriptionHtml={descriptionHtml}
                    onDescriptionHtmlChange={(nextHtml) => setDraftDescriptionHtml(field.id, nextHtml)}
                    commitDescriptionHtml={() => commitDraftDescriptionToSchema(field.id)}
                    enableValidation={field.enableValidation}
                    accessLevel={accessLevel}
                    isEditMode={!submitMode && !previewMode}
                    isFormArchived={isFormArchived}
                    rows={field.rows}
                    columns={field.columns}
                    allowCustomRows={field.allowCustomRows}
                    readOnly={readOnly}
                    type={field.type}
                    placeholderValues={placeholderValues}
                    audience={audience}
                    recipientType={recipientType}
                    linkedToStudents={linkedToStudents}
                    linkedToEmployees={linkedToEmployees}
                    error={error}
                    submitMode={submitMode}
                />
            );
        }

        case 'checkboxQuestion': {
            const fallbackDescriptionHtml = field.formattedDescription || field.description || '';
            const descriptionHtml = getDraftDescriptionHtml(field.id, fallbackDescriptionHtml);

            return (
                <CheckboxQuestion
                    componentId={field.id}
                    fieldKey={field.key}
                    descriptionHtml={descriptionHtml}
                    onDescriptionHtmlChange={(nextHtml) => setDraftDescriptionHtml(field.id, nextHtml)}
                    commitDescriptionHtml={() => commitDraftDescriptionToSchema(field.id)}
                    enableValidation={field.enableValidation}
                    value={readOnly ? data[field.id] : submitData[field.id]}
                    onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                    isEditMode={!submitMode && !previewMode}
                    isFormArchived={isFormArchived}
                    readOnly={readOnly}
                    placeholderValues={placeholderValues}
                    accessLevel={accessLevel}
                    submitMode={submitMode}
                    error={error}
                    audience={audience}
                    recipientType={recipientType}
                    linkedToStudents={linkedToStudents}
                    linkedToEmployees={linkedToEmployees}
                    index={index}
                />
            );
        }
        case 'additionalNotes': {
            const fallbackDescriptionHtml = field.formattedDescription || field.description || '';
            const descriptionHtml = getDraftDescriptionHtml(field.id, fallbackDescriptionHtml);
            return (
                <AdditionalNotes
                    componentId={field.id}
                    // Application, acceptance and public forms are not emailed, so their first Notes is not an email.
                    isEmailText={index === 0 && accessLevel !== FORM_ACCESS_LEVELS.public}
                    isBuilderView={!submitMode && !formPreviewMode}
                    descriptionHtml={descriptionHtml}
                    onDescriptionHtmlChange={(next) => setDraftDescriptionHtml(field.id, next)}
                    commitDescriptionHtml={() => commitDraftDescriptionToSchema(field.id)}
                    isEditMode={!submitMode && !previewMode}
                    isFormArchived={isFormArchived}
                    accessLevel={accessLevel}
                    audience={audience}
                    recipientType={recipientType}
                    linkedToStudents={linkedToStudents}
                    linkedToEmployees={linkedToEmployees}
                    placeholderValues={placeholderValues}
                    readOnly={readOnly}
                    submitMode={submitMode}
                />
            );
        }
        case 'multipleChoice': {
            const fallbackDescriptionHtml = field.formattedDescription || field.description || '';
            const descriptionHtml = getDraftDescriptionHtml(field.id, fallbackDescriptionHtml);
            return (
                <MultipleChoice
                    index={index}
                    componentId={field.id}
                    fieldKey={field.key}
                    onlyOneCategory={field.onlyOneCategory === true}
                    options={field.options}
                    value={readOnly ? data[field.id] : submitData[field.id]}
                    descriptionHtml={descriptionHtml}
                    onDescriptionHtmlChange={(nextHtml) => setDraftDescriptionHtml(field.id, nextHtml)}
                    commitDescriptionHtml={() => commitDraftDescriptionToSchema(field.id)}
                    enableValidation={field.enableValidation}
                    isEditMode={!submitMode && !previewMode}
                    isFormArchived={isFormArchived}
                    relatedComponent={relatedComponent}
                    accessLevel={accessLevel}
                    readOnly={readOnly}
                    placeholderValues={placeholderValues}
                    error={error}
                    audience={audience}
                    recipientType={recipientType}
                    linkedToStudents={linkedToStudents}
                    linkedToEmployees={linkedToEmployees}
                    submitMode={submitMode}
                    onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                />
            );
        }
        case 'singleChoice': {
            const fallbackDescriptionHtml = field.formattedDescription || field.description || '';
            const descriptionHtml = getDraftDescriptionHtml(field.id, fallbackDescriptionHtml);
            return (
                <SingleChoice
                    index={index}
                    componentId={field.id}
                    fieldKey={field.key}
                    options={field.options}
                    value={readOnly ? data[field.id] : submitData[field.id]}
                    descriptionHtml={descriptionHtml}
                    onDescriptionHtmlChange={(nextHtml) => setDraftDescriptionHtml(field.id, nextHtml)}
                    commitDescriptionHtml={() => commitDraftDescriptionToSchema(field.id)}
                    enableValidation={field.enableValidation}
                    isEditMode={!submitMode && !previewMode}
                    isFormArchived={isFormArchived}
                    relatedComponent={relatedComponent}
                    accessLevel={accessLevel}
                    readOnly={readOnly}
                    placeholderValues={placeholderValues}
                    error={error}
                    audience={audience}
                    recipientType={recipientType}
                    linkedToStudents={linkedToStudents}
                    linkedToEmployees={linkedToEmployees}
                    submitMode={submitMode}
                    onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                />
            );
        }
        case 'linearScale': {
            const fallbackDescriptionHtml = field.formattedDescription || field.description || '';
            const descriptionHtml = getDraftDescriptionHtml(field.id, fallbackDescriptionHtml);
            return (
                <LinearScale
                    onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                    componentToEditIndex={componentToEditIndex}
                    index={index}
                    componentId={field.id}
                    startFrom={field.startFrom}
                    endAt={field.endAt}
                    startLabel={field.startLabel}
                    endLabel={field.endLabel}
                    value={readOnly ? data[field.id] : submitData[field.id]}
                    descriptionHtml={descriptionHtml}
                    onDescriptionHtmlChange={(nextHtml) => setDraftDescriptionHtml(field.id, nextHtml)}
                    commitDescriptionHtml={() => commitDraftDescriptionToSchema(field.id)}
                    enableValidation={field.enableValidation}
                    submitMode={submitMode}
                    isEditMode={!submitMode && !previewMode}
                    isFormArchived={isFormArchived}
                    isSubmitted={readOnly && submitMode}
                    accessLevel={accessLevel}
                    readOnly={readOnly}
                    placeholderValues={placeholderValues}
                    audience={audience}
                    recipientType={recipientType}
                    linkedToStudents={linkedToStudents}
                    linkedToEmployees={linkedToEmployees}
                />
            );
        }
        case 'dropdown': {
            const fallbackDescriptionHtml = field.formattedDescription || field.description || '';
            const descriptionHtml = getDraftDescriptionHtml(field.id, fallbackDescriptionHtml);
            return (
                <Dropdown
                    onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                    componentToEditIndex={componentToEditIndex}
                    index={index}
                    componentId={field.id}
                    options={field.options}
                    value={readOnly ? data[field.id] : submitData[field.id]}
                    descriptionHtml={descriptionHtml}
                    onDescriptionHtmlChange={(nextHtml) => setDraftDescriptionHtml(field.id, nextHtml)}
                    commitDescriptionHtml={() => commitDraftDescriptionToSchema(field.id)}
                    enableValidation={field.enableValidation}
                    submitMode={submitMode}
                    isEditMode={!submitMode && !previewMode}
                    isFormArchived={isFormArchived}
                    relatedComponent={relatedComponent}
                    accessLevel={accessLevel}
                    readOnly={readOnly}
                    placeholderValues={placeholderValues}
                    error={error}
                    audience={audience}
                    recipientType={recipientType}
                    linkedToStudents={linkedToStudents}
                    linkedToEmployees={linkedToEmployees}
                />
            );
        }
        case 'lookupDropdown': {
            const fallbackDescriptionHtml = field.formattedDescription || field.description || '';
            const descriptionHtml = getDraftDescriptionHtml(field.id, fallbackDescriptionHtml);
            return (
                <LookupDropdown
                    onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                    componentToEditIndex={componentToEditIndex}
                    index={index}
                    componentId={field.id}
                    lookupType={field.lookupType}
                    fieldKey={field.key}
                    value={readOnly ? data[field.id] : submitData[field.id]}
                    descriptionHtml={descriptionHtml}
                    onDescriptionHtmlChange={(nextHtml) => setDraftDescriptionHtml(field.id, nextHtml)}
                    commitDescriptionHtml={() => commitDraftDescriptionToSchema(field.id)}
                    enableValidation={field.enableValidation}
                    submitMode={submitMode}
                    isEditMode={!submitMode && !previewMode}
                    isFormArchived={isFormArchived}
                    readOnly={readOnly}
                    placeholderValues={placeholderValues}
                    error={error}
                    audience={audience}
                    recipientType={recipientType}
                    linkedToStudents={linkedToStudents}
                    linkedToEmployees={linkedToEmployees}
                    accessLevel={accessLevel}
                />
            );
        }
        case 'attachment': {
            const fallbackDescriptionHtml = field.formattedDescription || field.description || '';
            const descriptionHtml = getDraftDescriptionHtml(field.id, fallbackDescriptionHtml);
            return (
                <Attachment
                    componentId={field.id}
                    onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                    componentToEditIndex={componentToEditIndex}
                    index={index}
                    submitMode={submitMode}
                    descriptionHtml={descriptionHtml}
                    onDescriptionHtmlChange={(nextHtml) => setDraftDescriptionHtml(field.id, nextHtml)}
                    commitDescriptionHtml={() => commitDraftDescriptionToSchema(field.id)}
                    attachments={field.attachments}
                    accessLevel={accessLevel}
                    isEditMode={!submitMode && !previewMode}
                    isFormArchived={isFormArchived}
                    readOnly={readOnly}
                    placeholderValues={placeholderValues}
                    audience={audience}
                    recipientType={recipientType}
                    linkedToStudents={linkedToStudents}
                    linkedToEmployees={linkedToEmployees}
                />
            );
        }
        case 'image': {
            const fallbackDescriptionHtml = field.formattedDescription || field.description || '';
            const descriptionHtml = getDraftDescriptionHtml(field.id, fallbackDescriptionHtml);
            return (
                <Image
                    componentId={field.id}
                    onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                    componentToEditIndex={componentToEditIndex}
                    index={index}
                    submitMode={submitMode}
                    descriptionHtml={descriptionHtml}
                    onDescriptionHtmlChange={(nextHtml) => setDraftDescriptionHtml(field.id, nextHtml)}
                    commitDescriptionHtml={() => commitDraftDescriptionToSchema(field.id)}
                    attachments={field.attachments}
                    imageWidthPercent={field.imageWidthPercent}
                    accessLevel={accessLevel}
                    isEditMode={!submitMode && !previewMode}
                    isFormArchived={isFormArchived}
                    readOnly={readOnly}
                    placeholderValues={placeholderValues}
                    audience={audience}
                    recipientType={recipientType}
                    linkedToStudents={linkedToStudents}
                    linkedToEmployees={linkedToEmployees}
                />
            );
        }
        case 'signature': {
            const handleSignatureChange = !submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit;
            const fallbackDescriptionHtml = field.formattedDescription || field.description || '';
            const descriptionHtml = getDraftDescriptionHtml(field.id, fallbackDescriptionHtml);
            return (
                <Signature
                    onChange={handleSignatureChange}
                    id={field.id}
                    index={index}
                    value={readOnly ? data[field.id] : submitData[field.id]}
                    isEditMode={!submitMode && !previewMode}
                    isSubmitted={readOnly && submitMode}
                    error={error}
                    descriptionHtml={descriptionHtml}
                    onDescriptionHtmlChange={(nextHtml) => setDraftDescriptionHtml(field.id, nextHtml)}
                    commitDescriptionHtml={() => commitDraftDescriptionToSchema(field.id)}
                    isFormArchived={isFormArchived}
                    accessLevel={accessLevel}
                    placeholderValues={placeholderValues}
                    readOnly={readOnly}
                    submitMode={submitMode}
                    audience={audience}
                    recipientType={recipientType}
                    linkedToStudents={linkedToStudents}
                    linkedToEmployees={linkedToEmployees}
                />
            );
        }
        case FormBuilderComponentTypes.OfferStatusChoice: {
            const fallbackDescriptionHtml = field.formattedDescription || field.description || '';
            const descriptionHtml = getDraftDescriptionHtml(field.id, fallbackDescriptionHtml);
            return (
                <OfferStatusChoice
                    componentId={field.id}
                    index={index}
                    options={field.options}
                    value={readOnly ? data[field.id] : submitData[field.id]}
                    descriptionHtml={descriptionHtml}
                    onDescriptionHtmlChange={(nextHtml) => setDraftDescriptionHtml(field.id, nextHtml)}
                    commitDescriptionHtml={() => commitDraftDescriptionToSchema(field.id)}
                    onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                    enableValidation={field.enableValidation}
                    isEditMode={!submitMode && !previewMode}
                    isFormArchived={isFormArchived}
                    relatedComponent={relatedComponent}
                    accessLevel={accessLevel}
                    readOnly={readOnly}
                    placeholderValues={placeholderValues}
                    audience={audience}
                    parentAudienceType={parentAudienceType}
                    recipientType={recipientType}
                    linkedToStudents={linkedToStudents}
                    linkedToEmployees={linkedToEmployees}
                    formPreviewMode={formPreviewMode}
                    submitMode={submitMode}
                />
            );
        }
        case FormBuilderComponentTypes.FileUploader: {
            const fallbackDescriptionHtml = field.formattedDescription || field.description || '';
            const descriptionHtml = getDraftDescriptionHtml(field.id, fallbackDescriptionHtml);
            return (
                <FileUploader
                    componentId={field.id}
                    index={index}
                    value={readOnly ? data[field.id] : submitData[field.id]}
                    descriptionHtml={descriptionHtml}
                    onDescriptionHtmlChange={(nextHtml) => setDraftDescriptionHtml(field.id, nextHtml)}
                    commitDescriptionHtml={() => commitDraftDescriptionToSchema(field.id)}
                    onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                    isFormArchived={isFormArchived}
                    accessLevel={accessLevel}
                    isEditMode={!submitMode && !previewMode}
                    readOnly={readOnly}
                    placeholderValues={placeholderValues}
                    enableValidation={field.enableValidation}
                    handleDownloadAttachment={handleDownloadAttachment}
                    error={error}
                    submitMode={submitMode}
                    audience={audience}
                    recipientType={recipientType}
                    linkedToStudents={linkedToStudents}
                    linkedToEmployees={linkedToEmployees}
                />
            );
        }
        case 'dateTime': {
            const fallbackDescriptionHtml = field.formattedDescription || field.description || '';
            const descriptionHtml = getDraftDescriptionHtml(field.id, fallbackDescriptionHtml);
            return (
                <DateTime
                    onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                    index={index}
                    componentId={field.id}
                    value={readOnly ? data[field.id] : submitData[field.id]}
                    descriptionHtml={descriptionHtml}
                    onDescriptionHtmlChange={(nextHtml) => setDraftDescriptionHtml(field.id, nextHtml)}
                    commitDescriptionHtml={() => commitDraftDescriptionToSchema(field.id)}
                    enableValidation={field.enableValidation}
                    accessLevel={accessLevel}
                    isEditMode={!submitMode && !previewMode}
                    isFormArchived={isFormArchived}
                    readOnly={readOnly}
                    placeholderValues={placeholderValues}
                    submitMode={submitMode}
                    minDateTime={field.minDateTime}
                    maxDateTime={field.maxDateTime}
                    error={error}
                    audience={audience}
                    recipientType={recipientType}
                    linkedToStudents={linkedToStudents}
                    linkedToEmployees={linkedToEmployees}
                />
            );
        }
        case 'date': {
            const fallbackDescriptionHtml = field.formattedDescription || field.description || '';
            const descriptionHtml = getDraftDescriptionHtml(field.id, fallbackDescriptionHtml);

            return (
                <Date
                    onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                    index={index}
                    componentId={field.id}
                    fieldKey={field.key}
                    value={readOnly ? data[field.id] : submitData[field.id]}
                    descriptionHtml={descriptionHtml}
                    onDescriptionHtmlChange={(nextHtml) => setDraftDescriptionHtml(field.id, nextHtml)}
                    commitDescriptionHtml={() => commitDraftDescriptionToSchema(field.id)}
                    enableValidation={field.enableValidation}
                    accessLevel={accessLevel}
                    isEditMode={!submitMode && !previewMode}
                    isFormArchived={isFormArchived}
                    readOnly={readOnly}
                    placeholderValues={placeholderValues}
                    submitMode={submitMode}
                    minAge={field.minAge}
                    error={error}
                    audience={audience}
                    recipientType={recipientType}
                    linkedToStudents={linkedToStudents}
                    linkedToEmployees={linkedToEmployees}
                />
            );
        }
        case FormBuilderComponentTypes.Address: {
            const fallbackDescriptionHtml = field.formattedDescription || field.description || '';
            const descriptionHtml = getDraftDescriptionHtml(field.id, fallbackDescriptionHtml);

            return (
                <Address
                    componentId={field.id}
                    componentToEditIndex={componentToEditIndex}
                    index={index}
                    enableValidation={field.enableValidation}
                    descriptionHtml={descriptionHtml}
                    onDescriptionHtmlChange={(nextHtml) => setDraftDescriptionHtml(field.id, nextHtml)}
                    commitDescriptionHtml={() => commitDraftDescriptionToSchema(field.id)}
                    value={readOnly ? data[field.id] : submitData[field.id]}
                    onEnableValidationChange={(nextEnabled) =>
                        handleChangeEdit({
                            index,
                            enableValidation: nextEnabled
                        })
                    }
                    onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                    isEditMode={!submitMode && !previewMode}
                    isFormArchived={isFormArchived}
                    readOnly={readOnly}
                    placeholderValues={placeholderValues}
                    accessLevel={accessLevel}
                    error={error}
                    audience={audience}
                    recipientType={recipientType}
                    linkedToStudents={linkedToStudents}
                    linkedToEmployees={linkedToEmployees}
                    submitMode={submitMode}
                />
            );
        }
        case FormBuilderComponentTypes.IntakeChoice: {
            const fallbackDescriptionHtml = field.formattedDescription || field.description || '';
            const descriptionHtml = getDraftDescriptionHtml(field.id, fallbackDescriptionHtml);

            return (
                <IntakeChoice
                    componentId={field.id}
                    index={index}
                    fieldKey={field.key}
                    options={field.options}
                    value={readOnly ? data[field.id] : submitData[field.id]}
                    enableValidation={field.enableValidation}
                    applicationStatusId={field.applicationStatusId}
                    descriptionHtml={descriptionHtml}
                    onDescriptionHtmlChange={(nextHtml) => setDraftDescriptionHtml(field.id, nextHtml)}
                    commitDescriptionHtml={() => commitDraftDescriptionToSchema(field.id)}
                    onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                    isEditMode={!submitMode && !previewMode}
                    isSubmitMode={submitMode}
                    isFormPreviewMode={formPreviewMode}
                    isFormArchived={isFormArchived}
                    relatedComponent={relatedComponent}
                    accessLevel={accessLevel}
                    readOnly={readOnly}
                    placeholderValues={placeholderValues}
                    error={error}
                    audience={audience}
                    recipientType={recipientType}
                    linkedToStudents={linkedToStudents}
                    linkedToEmployees={linkedToEmployees}
                    submitMode={submitMode}
                />
            );
        }
        case FormBuilderComponentTypes.BoarderStatusChoice: {
            const fallbackDescriptionHtml = field.formattedDescription || field.description || '';
            const descriptionHtml = getDraftDescriptionHtml(field.id, fallbackDescriptionHtml);

            return (
                <BoarderStatusChoice
                    componentId={field.id}
                    index={index}
                    fieldKey={field.key}
                    options={field.options}
                    value={readOnly ? data[field.id] : submitData[field.id]}
                    enableValidation={field.enableValidation}
                    descriptionHtml={descriptionHtml}
                    onDescriptionHtmlChange={(nextHtml) => setDraftDescriptionHtml(field.id, nextHtml)}
                    commitDescriptionHtml={() => commitDraftDescriptionToSchema(field.id)}
                    onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                    isEditMode={!submitMode && !previewMode}
                    isSubmitMode={submitMode}
                    isFormPreviewMode={formPreviewMode}
                    isFormArchived={isFormArchived}
                    relatedComponent={relatedComponent}
                    accessLevel={accessLevel}
                    readOnly={readOnly}
                    placeholderValues={placeholderValues}
                    error={error}
                    audience={audience}
                    recipientType={recipientType}
                    linkedToStudents={linkedToStudents}
                    linkedToEmployees={linkedToEmployees}
                    submitMode={submitMode}
                />
            );
        }
        case FormBuilderComponentTypes.QuestionGrid: {
            const fallbackDescriptionHtml = field.formattedDescription || field.description || '';
            const descriptionHtml = getDraftDescriptionHtml(field.id, fallbackDescriptionHtml);
            return (
                <QuestionGrid
                    componentId={field.id}
                    index={index}
                    value={readOnly ? data[field.id] : submitData[field.id]}
                    inputs={field.inputs}
                    columns={field.columns}
                    allowCustomRows={field.allowCustomRows}
                    descriptionHtml={descriptionHtml}
                    onDescriptionHtmlChange={(nextHtml) => setDraftDescriptionHtml(field.id, nextHtml)}
                    commitDescriptionHtml={() => commitDraftDescriptionToSchema(field.id)}
                    onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                    isEditMode={!submitMode && !previewMode}
                    isFormArchived={isFormArchived}
                    readOnly={readOnly}
                    placeholderValues={placeholderValues}
                    accessLevel={accessLevel}
                    error={error}
                    submitMode={submitMode}
                    audience={audience}
                    recipientType={recipientType}
                    linkedToStudents={linkedToStudents}
                    linkedToEmployees={linkedToEmployees}
                />
            );
        }
        case FormBuilderComponentTypes.Separator: {
            return (
                <Separator
                    field={field}
                    isEditMode={!submitMode && !previewMode}
                    submitMode={submitMode}
                    onChange={handleChangeEdit}
                    index={index}
                />
            );
        }
        case FormBuilderComponentTypes.UPN: {
            const fallbackDescriptionHtml = field.formattedDescription || field.description || '';
            const descriptionHtml = getDraftDescriptionHtml(field.id, fallbackDescriptionHtml);

            return (
                <UPN
                    componentId={field.id}
                    index={index}
                    fieldKey={field.key}
                    descriptionHtml={descriptionHtml}
                    onDescriptionHtmlChange={(nextHtml) => setDraftDescriptionHtml(field.id, nextHtml)}
                    commitDescriptionHtml={() => commitDraftDescriptionToSchema(field.id)}
                    value={readOnly ? data[field.id] : submitData[field.id]}
                    onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                    enableValidation={field.enableValidation}
                    isEditMode={!submitMode && !previewMode}
                    isFormArchived={isFormArchived}
                    readOnly={readOnly}
                    placeholderValues={placeholderValues}
                    submitMode={submitMode}
                    accessLevel={accessLevel}
                    error={error}
                    audience={audience}
                    recipientType={recipientType}
                    linkedToStudents={linkedToStudents}
                    linkedToEmployees={linkedToEmployees}
                />
            );
        }
        case FormBuilderComponentTypes.PreviousSchoolName: {
            const fallbackDescriptionHtml = field.formattedDescription || field.description || '';
            const descriptionHtml = getDraftDescriptionHtml(field.id, fallbackDescriptionHtml);

            return (
                <PreviousSchoolName
                    componentId={field.id}
                    index={index}
                    fieldKey={field.key}
                    descriptionHtml={descriptionHtml}
                    onDescriptionHtmlChange={(nextHtml) => setDraftDescriptionHtml(field.id, nextHtml)}
                    commitDescriptionHtml={() => commitDraftDescriptionToSchema(field.id)}
                    value={readOnly ? data[field.id] : submitData[field.id]}
                    onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                    enableValidation={field.enableValidation}
                    isEditMode={!submitMode && !previewMode}
                    isFormArchived={isFormArchived}
                    readOnly={readOnly}
                    placeholderValues={placeholderValues}
                    submitMode={submitMode}
                    accessLevel={accessLevel}
                    error={error}
                    audience={audience}
                    recipientType={recipientType}
                    linkedToStudents={linkedToStudents}
                    linkedToEmployees={linkedToEmployees}
                />
            );
        }
        case FormBuilderComponentTypes.PreviousSchoolEmail: {
            const fallbackDescriptionHtml = field.formattedDescription || field.description || '';
            const descriptionHtml = getDraftDescriptionHtml(field.id, fallbackDescriptionHtml);

            return (
                <PreviousSchoolEmail
                    componentId={field.id}
                    index={index}
                    fieldKey={field.key}
                    descriptionHtml={descriptionHtml}
                    onDescriptionHtmlChange={(nextHtml) => setDraftDescriptionHtml(field.id, nextHtml)}
                    commitDescriptionHtml={() => commitDraftDescriptionToSchema(field.id)}
                    value={readOnly ? data[field.id] : submitData[field.id]}
                    onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                    enableValidation={field.enableValidation}
                    isEditMode={!submitMode && !previewMode}
                    isFormArchived={isFormArchived}
                    readOnly={readOnly}
                    placeholderValues={placeholderValues}
                    submitMode={submitMode}
                    accessLevel={accessLevel}
                    error={error}
                    audience={audience}
                    recipientType={recipientType}
                    linkedToStudents={linkedToStudents}
                    linkedToEmployees={linkedToEmployees}
                />
            );
        }
        case FormBuilderComponentTypes.EmployeeSelect: {
            const fallbackDescriptionHtml = field.formattedDescription || field.description || '';
            const descriptionHtml = getDraftDescriptionHtml(field.id, fallbackDescriptionHtml);

            return (
                <EmployeeSelect
                    componentId={field.id}
                    index={index}
                    descriptionHtml={descriptionHtml}
                    onDescriptionHtmlChange={(nextHtml) => setDraftDescriptionHtml(field.id, nextHtml)}
                    commitDescriptionHtml={() => commitDraftDescriptionToSchema(field.id)}
                    value={readOnly ? data[field.id] : submitData[field.id]}
                    onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                    enableValidation={field.enableValidation}
                    isEditMode={!submitMode && !previewMode}
                    isFormArchived={isFormArchived}
                    readOnly={readOnly}
                    placeholderValues={placeholderValues}
                    submitMode={submitMode}
                    accessLevel={accessLevel}
                    error={error}
                    audience={audience}
                    recipientType={recipientType}
                    linkedToStudents={linkedToStudents}
                    linkedToEmployees={linkedToEmployees}
                />
            );
        }
        case FormBuilderComponentTypes.StudentPhoto: {
            const fallbackDescriptionHtml = field.formattedDescription || field.description || '';
            const descriptionHtml = getDraftDescriptionHtml(field.id, fallbackDescriptionHtml);

            return (
                <StudentPhoto
                    componentId={field.id}
                    field={field}
                    index={index}
                    descriptionHtml={descriptionHtml}
                    onDescriptionHtmlChange={(nextHtml) => setDraftDescriptionHtml(field.id, nextHtml)}
                    commitDescriptionHtml={() => commitDraftDescriptionToSchema(field.id)}
                    onChange={handleChangeEdit}
                    isEditMode={!submitMode && !previewMode}
                    isFormArchived={isFormArchived}
                    readOnly={readOnly}
                    placeholderValues={placeholderValues}
                    submitMode={submitMode}
                    accessLevel={accessLevel}
                    audience={audience}
                    recipientType={recipientType}
                    linkedToStudents={linkedToStudents}
                    linkedToEmployees={linkedToEmployees}
                />
            );
        }
        default:
            return null;
    }
    };

    const content = renderComponent();
    const canShowPrefill =
        !submitMode &&
        !formPreviewMode &&
        PREFILLABLE_COMPONENT_TYPES.has(field?.type);

    if (!canShowPrefill) {
        return content;
    }

    // previewMode=false means this component is open for editing in the builder
    const isComponentEditMode = !previewMode;

    return (
        <Box>
            {content}
            <PrefillFromSettings
                index={index}
                field={field}
                formId={formId}
                accessLevel={accessLevel}
                isFormArchived={isFormArchived}
                isEditMode={isComponentEditMode}
                onChange={handleChangeEdit}
            />
        </Box>
    );
};

ComponentBuilder.propTypes = {
    index: PropTypes.number,
    readOnly: PropTypes.bool,
    previewMode: PropTypes.bool,
    formPreviewMode: PropTypes.bool,
    handleChangeEdit: PropTypes.func,
    handleChangeSubmit: PropTypes.func,
    handleDownloadAttachment: PropTypes.func,
    submitData: PropTypes.object,
    data: PropTypes.object,
    submitMode: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    field: PropTypes.object,
    placeholderValues: PropTypes.object,
    accessLevel: PropTypes.number,
    relatedComponent: PropTypes.object,
    componentToEditIndex: PropTypes.number,
    error: PropTypes.any,
    audience: PropTypes.number,
    parentAudienceType: PropTypes.number,
    recipientType: PropTypes.string,
    linkedToStudents: PropTypes.bool,
    linkedToEmployees: PropTypes.bool,
    formRequestId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    formId: PropTypes.number,
    schema: PropTypes.array,
    onEmailCheck: PropTypes.func,
    formAccessLevel: PropTypes.number,
    warnings: PropTypes.object,
    setWarningFor: PropTypes.func,
    componentId: PropTypes.any,
    getDraftDescriptionHtml: PropTypes.func,
    setDraftDescriptionHtml: PropTypes.func,
    commitDraftDescriptionToSchema: PropTypes.func,
};

export default ComponentBuilder;
